import { Text } from '@chakra-ui/react';
import { Slider } from 'antd';
import cn from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import { useFinanceFilter } from '@/hooks/filters/finance-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';

import { PriceFormat } from '../../../../price-format/price-format';
import { InputsSeparator } from '../inputs-separator/inputs-separator';
import { PriceFilterPromo } from '../price-filter-promo/price-filter-promo';
import { PriceInput } from '../price-input/price-input';
import { SectionName } from '../section-name/section-name';

import styles from './price.module.scss';

const cx = cn.bind(styles);

export const Price: React.FC<{
  showInCarFinder?: boolean;
  defaultPriceFilter: { min: number; max: number };
}> = ({ showInCarFinder, defaultPriceFilter }) => {
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const { priceFilter, setPriceFilter, setSelectedPriceFilter } =
    usePriceFilter();
  const { setFinanceFilter } = useFinanceFilter();
  const { isCleared } = useFiltersQuery();

  const marks = {
    [defaultPriceFilter.min]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat prefix="R" value={defaultPriceFilter.min} />
        </Text>
      ),
    },
    [defaultPriceFilter.max]: {
      label: (
        <Text className={styles.priceFormatText}>
          <PriceFormat prefix="R" value={defaultPriceFilter.max} />
        </Text>
      ),
    },
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(true);

      return;
    }

    setPriceFilter({
      min: defaultPriceFilter.min,
      max: defaultPriceFilter.max,
    });
  }, [isCleared]);

  const handleMinValue = (value: number) => {
    if (value > priceFilter.max || value < defaultPriceFilter.min) {
      return;
    }

    setPriceFilter((previousPriceFilter) => ({
      ...previousPriceFilter,
      min: value,
    }));

    setSelectedPriceFilter({
      ...priceFilter,
      min: value,
    });
  };

  const handleMaxValue = (value: number) => {
    if (value < priceFilter.min || value > defaultPriceFilter.max) {
      return;
    }

    setPriceFilter(() => ({
      min: priceFilter.min,
      max: value,
    }));

    setSelectedPriceFilter({
      ...priceFilter,
      max: value,
    });

    setFinanceFilter((previousFinanceFilter) => ({
      ...previousFinanceFilter,
      price: value,
    }));
  };

  return (
    <div
      className={cx(styles.priceWrapper, {
        carFinderWrapper: Boolean(showInCarFinder),
      })}
    >
      <div className={styles.partWrapper}>
        <div className={styles.sectionWrapper}>
          <SectionName>Price Range</SectionName>
          <div
            className={cx(styles.rangesWrapper, {
              rangesCarFinderWrapper: showInCarFinder,
            })}
          >
            <PriceInput
              prefix="R"
              value={priceFilter.min || defaultPriceFilter.min}
              onChange={handleMinValue}
              name="minPrice"
              step={5000}
            />
            <InputsSeparator />
            <PriceInput
              prefix="R"
              value={priceFilter.max || defaultPriceFilter.max}
              onChange={handleMaxValue}
              name="maxPrice"
              step={5000}
            />
          </div>
        </div>
        <Slider
          className={styles.sliderRange}
          range
          marks={marks}
          step={10000}
          max={defaultPriceFilter.max}
          value={[
            priceFilter.min || defaultPriceFilter.min,
            priceFilter.max || defaultPriceFilter.max,
          ]}
          tipFormatter={null}
          onChange={([min, max]) => {
            setPriceFilter({
              min: min ?? defaultPriceFilter.min,
              max: max ?? defaultPriceFilter.max,
            });

            setSelectedPriceFilter({
              min: min ?? defaultPriceFilter.min,
              max: max ?? defaultPriceFilter.max,
            });
            setFinanceFilter((previousFinanceFilter) => ({
              ...previousFinanceFilter,
              price: max ?? defaultPriceFilter.max,
            }));
          }}
        />
      </div>
      {!showInCarFinder && (
        <div className={styles.partWrapper}>
          <PriceFilterPromo />
        </div>
      )}
    </div>
  );
};
